// PagesTabBar.jsx
// Power BI-style tab strip for multi-page dashboards. Each dashboard has an
// implicit "default" page; explicit pages are stored on Dashboard.pages[].
// Panels reference pages via panel.pageId.

import React, { useState } from 'react';
import { Plus, X, Edit2 } from 'lucide-react';

const DEFAULT_PAGE = { id: 'default', name: 'Page 1', order: 0 };

export function normalizePages(pages) {
  if (!Array.isArray(pages) || pages.length === 0) return [DEFAULT_PAGE];
  return [...pages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export default function PagesTabBar({
  pages = [], activePageId, onChangeActive,
  onAddPage, onRenamePage, onDeletePage,
  darkMode = true, readOnly = false
}) {
  const list = normalizePages(pages);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const T = darkMode ? {
    text: '#e5e7eb', mute: '#94a3b8', accent: '#8b5cf6',
    tabBg: 'transparent', tabActiveBg: '#0f172a',
    border: '#1e293b', borderActive: '#8b5cf6',
    hover: 'rgba(139,92,246,0.08)', danger: '#f87171'
  } : {
    text: '#0f172a', mute: '#64748b', accent: '#7c3aed',
    tabBg: 'transparent', tabActiveBg: '#ffffff',
    border: '#e2e8f0', borderActive: '#7c3aed',
    hover: 'rgba(124,58,237,0.06)', danger: '#dc2626'
  };

  const commitRename = () => {
    if (editingId && editValue.trim()) {
      onRenamePage?.(editingId, editValue.trim());
    }
    setEditingId(null);
    setEditValue('');
  };

  const startAdd = () => {
    const name = window.prompt('New page name:');
    if (!name || !name.trim()) return;
    onAddPage?.(name.trim());
  };

  const startDelete = (page) => {
    if (list.length === 1) {
      alert('A dashboard must have at least one page.');
      return;
    }
    if (!window.confirm(`Delete page "${page.name}" and all its panels?`)) return;
    onDeletePage?.(page.id);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 2,
      borderBottom: `1px solid ${T.border}`, padding: '0 8px',
      overflowX: 'auto', flexShrink: 0
    }}>
      {list.map(p => {
        const isActive = p.id === activePageId;
        const isEditing = editingId === p.id;
        return (
          <div key={p.id} style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '6px 4px 6px 10px', gap: 4, cursor: 'pointer',
            background: isActive ? T.tabActiveBg : T.tabBg,
            borderBottom: `2px solid ${isActive ? T.borderActive : 'transparent'}`,
            marginBottom: -1, flexShrink: 0,
            transition: 'background 0.12s ease'
          }}
          onClick={() => !isEditing && onChangeActive?.(p.id)}
          onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = T.hover; }}
          onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = T.tabBg; }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (readOnly) return;
            setEditingId(p.id); setEditValue(p.name);
          }}
          >
            {isEditing ? (
              <input
                autoFocus
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') { setEditingId(null); setEditValue(''); }
                }}
                onClick={e => e.stopPropagation()}
                style={{
                  background: 'transparent', border: `1px solid ${T.accent}`,
                  borderRadius: 4, color: T.text, fontSize: 12, fontWeight: 600,
                  padding: '2px 6px', outline: 'none', minWidth: 60, maxWidth: 160
                }}
              />
            ) : (
              <span style={{
                fontSize: 12, fontWeight: isActive ? 700 : 500,
                color: isActive ? T.text : T.mute, whiteSpace: 'nowrap'
              }}
              title={readOnly ? p.name : `${p.name} (double-click to rename)`}
              >
                {p.name}
              </span>
            )}
            {!readOnly && !isEditing && isActive && list.length > 1 && (
              <span
                onClick={(e) => { e.stopPropagation(); startDelete(p); }}
                title="Delete this page"
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: 2, borderRadius: 3, marginLeft: 2, color: T.mute
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = T.danger}
                onMouseLeave={(e) => e.currentTarget.style.color = T.mute}
              >
                <X size={11} />
              </span>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <button
          onClick={startAdd}
          title="Add a new page"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 10px', marginLeft: 4, marginBottom: -1,
            background: 'transparent', border: 'none',
            color: T.mute, cursor: 'pointer', fontSize: 12, fontWeight: 500
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = T.accent}
          onMouseLeave={(e) => e.currentTarget.style.color = T.mute}
        >
          <Plus size={12} /> Add page
        </button>
      )}
    </div>
  );
}
